/**
 * App Component
 * Root component handling routing and global configurations
 * Features:
 * - Protected route management
 * - Form flow testing utilities
 * - Global toast notifications
 * - localStorage cleanup on page unload
 * - Environment variable validation
 * - Development testing dashboard
 * - Mixpanel analytics tracking
 */

import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import LandingPage from './pages/LandingPage';
import EventDetails from './pages/EventDetails';
import DreamDescription from './pages/DreamDescription';
import InspirationUpload from './pages/InspirationUpload';
import Contact from './pages/Contact';
import SubmissionSuccess from './pages/SubmissionSuccess';
import RouteGuard from './components/RouteGuard';
import Mission from './pages/Mission';
import { Toaster } from 'react-hot-toast';
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react"
import Calculator from './pages/Calculator';
import NotFound from './pages/NotFound';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Venues from './pages/Venues';
import Inspo from './pages/Inspo';
import Mixpanel from './utils/mixpanel';
import ScrollToTop from './components/ScrollToTop';
import { DefaultSEO } from './components/SEO/DefaultSEO';
import GoogleAnalytics from './utils/googleAnalytics';
import FacebookPixel from './utils/facebookPixel';
import { storeUtmParameters } from './utils/utmParameters';
import { EventFormProvider } from './context/EventFormContext';

// Lazy load less frequently used pages
const Guides = lazy(() => import('./pages/Guides'));

// Lazy load guide pages
const NYCBirthdayVenues = lazy(() => import('./pages/guides/NYCBirthdayVenues'));
const NYCLargeVenues = lazy(() => import('./pages/guides/NYCLargeVenues'));
const NYCHappyHour = lazy(() => import('./pages/guides/NYCHappyHour'));
const NYCReservableBars = lazy(() => import('./pages/guides/NYCReservableBars'));
const Top5LargestNYCRehearsalDinnerVenues = lazy(() => import('./pages/guides/Top5LargestNYCRehearsalDinnerVenues'));
const NYCEventBudgetBreakdowns = lazy(() => import('./pages/guides/NYCEventBudgetBreakdowns'));
const VenueSelectionChecklist = lazy(() => import('./pages/guides/VenueSelectionChecklist'));
const VenuePricingModels = lazy(() => import('./pages/guides/VenuePricingModels'));
const FoodBeveragePlanning = lazy(() => import('./pages/guides/FoodBeveragePlanning'));
const EventPlanningTimeline = lazy(() => import('./pages/guides/EventPlanningTimeline'));
const LastMinuteVenueBooking = lazy(() => import('./pages/guides/LastMinuteVenueBooking'));
const NYCPrivateDining = lazy(() => import('./pages/guides/NYCPrivateDining'));
const NYCShabbatDinnerGuide = lazy(() => import('./pages/guides/NYCShabbatDinnerGuide'));
const NYCRehearsalDinnerGuide = lazy(() => import('./pages/guides/NYCRehearsalDinnerGuide'));
const NYCEngagementPartyVenues = lazy(() => import('./pages/guides/NYCEngagementPartyVenues'));
const NYCCocktailPartyVenues = lazy(() => import('./pages/guides/NYCCocktailPartyVenues'));
const NYCBusinessCorporateDinnerVenues = lazy(() => import('./pages/guides/NYCBusinessCorporateDinnerVenues'));
const NYCPrivateBirthdayDinner = lazy(() => import('./pages/guides/NYCPrivateBirthdayDinner'));
const NYCVenueHiddenFees = lazy(() => import('./pages/guides/NYCVenueHiddenFees'));
const NYCVenueCostPerPerson = lazy(() => import('./pages/guides/NYCVenueCostPerPerson'));
const NYCWeekendEngagementPartyVenues = lazy(() => import('./pages/guides/NYCWeekendEngagementPartyVenues'));
const NYCPrivateDining50Guests = lazy(() => import('./pages/guides/NYCPrivateDining50Guests'));

// Simple loading spinner component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
  </div>
);

// Conditionally import debug pages only in development
const LocationDebugPage = process.env.NODE_ENV === 'development' 
  ? require('./test-utils/LocationDebugPage').default 
  : null;


/**
 * Component to track page views for analytics
 */
function PageViewTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Capture and store UTM parameters on page load/navigation
    storeUtmParameters();
    
    // Simple analytics tracking
    const pageName = location.pathname;
    const pageTitle = document.title;
    
    // Track page view in Mixpanel
    Mixpanel.trackPageView(pageName);
    
    // Track page view in Google Analytics
    GoogleAnalytics.pageview(pageName, pageTitle);
    
    // Track page view in Facebook Pixel
    FacebookPixel.pageview();
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`Page Tracked: ${pageName}`, {
        title: pageTitle,
        url: window.location.href,
        referrer: document.referrer
      });
    }
  }, [location]);
  
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <EventFormProvider>
        <Router>
          <div className="App">
            <DefaultSEO />
            <PageViewTracker />
            <ScrollToTop />
          <Toaster 
            position="top-center"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#333',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#4ade80',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/mission" element={<Mission />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/venues" element={<Venues />} />
            <Route path="/inspo" element={<Inspo />} />
            <Route path="/calculator" element={<Calculator />} />
            {/* <Route path="/guestlist" element={<Guestlist />} /> */}
            
            {/* Guide routes - not shown in main navigation */}
            <Route path="/guides" element={
              <Suspense fallback={<LoadingFallback />}>
                <Guides />
              </Suspense>
            } />
            <Route path="/guides/nyc-birthday-venues" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCBirthdayVenues />
              </Suspense>
            } />
            <Route path="/guides/nyc-large-venues" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCLargeVenues />
              </Suspense>
            } />
            <Route path="/guides/nyc-happy-hour" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCHappyHour />
              </Suspense>
            } />
            <Route path="/guides/nyc-reservable-bars" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCReservableBars />
              </Suspense>
            } />
            <Route path="/guides/nyc-rehearsal-dinner-venues" element={
              <Suspense fallback={<LoadingFallback />}>
                <Top5LargestNYCRehearsalDinnerVenues />
              </Suspense>
            } />
            <Route path="/guides/nyc-event-budget-breakdowns" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCEventBudgetBreakdowns />
              </Suspense>
            } />
            <Route path="/guides/venue-selection-checklist" element={
              <Suspense fallback={<LoadingFallback />}>
                <VenueSelectionChecklist />
              </Suspense>
            } />
            <Route path="/guides/venue-pricing-models" element={
              <Suspense fallback={<LoadingFallback />}>
                <VenuePricingModels />
              </Suspense>
            } />
            <Route path="/guides/food-beverage-planning" element={
              <Suspense fallback={<LoadingFallback />}>
                <FoodBeveragePlanning />
              </Suspense>
            } />
            <Route path="/guides/event-planning-timeline" element={
              <Suspense fallback={<LoadingFallback />}>
                <EventPlanningTimeline />
              </Suspense>
            } />
            <Route path="/guides/last-minute-venue-booking" element={
              <Suspense fallback={<LoadingFallback />}>
                <LastMinuteVenueBooking />
              </Suspense>
            } />
            <Route path="/guides/nyc-private-dining" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCPrivateDining />
              </Suspense>
            } />
            <Route path="/guides/nyc-shabbat-dinner" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCShabbatDinnerGuide />
              </Suspense>
            } />
            <Route path="/guides/nyc-rehearsal-dinner" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCRehearsalDinnerGuide />
              </Suspense>
            } />
            <Route path="/guides/nyc-engagement-party-venues" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCEngagementPartyVenues />
              </Suspense>
            } />
            <Route path="/guides/nyc-cocktail-party-venues" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCCocktailPartyVenues />
              </Suspense>
            } />
            <Route path="/guides/nyc-business-corporate-dinner-venues" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCBusinessCorporateDinnerVenues />
              </Suspense>
            } />
            <Route path="/guides/nyc-private-birthday-dinner" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCPrivateBirthdayDinner />
              </Suspense>
            } />
            <Route path="/guides/nyc-venue-hidden-fees" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCVenueHiddenFees />
              </Suspense>
            } />
            <Route path="/guides/nyc-venue-cost-per-person" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCVenueCostPerPerson />
              </Suspense>
            } />
            <Route path="/guides/nyc-weekend-engagement-party-venues" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCWeekendEngagementPartyVenues />
              </Suspense>
            } />
            <Route path="/guides/nyc-private-dining-50-guests" element={
              <Suspense fallback={<LoadingFallback />}>
                <NYCPrivateDining50Guests />
              </Suspense>
            } />
            
            {/* Development debug routes - only available in development */}
            {process.env.NODE_ENV === 'development' && (
              <Route path="/debug-location" element={<LocationDebugPage />} />
            )}
            
            {/* Protected routes requiring previous step completion */}
            <Route path="/details" element={
              <RouteGuard requiredData="/details">
                <EventDetails />
              </RouteGuard>
            } />
            <Route path="/dream" element={
              <RouteGuard requiredData="/dream">
                <DreamDescription />
              </RouteGuard>
            } />
            <Route path="/inspiration" element={
              <RouteGuard requiredData="/inspiration">
                <InspirationUpload />
              </RouteGuard>
            } />
            <Route path="/contact" element={
              <RouteGuard requiredData="/contact">
                <Contact />
              </RouteGuard>
            } />
            <Route path="/submission-success" element={
              <RouteGuard requiredData="/submission-success">
                <SubmissionSuccess />
              </RouteGuard>
            } />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <SpeedInsights />
          <Analytics />
          </div>
        </Router>
      </EventFormProvider>
    </HelmetProvider>
  );
}
export default App;

